import { ApiErrorState } from '@/components/ApiErrorState/ApiErrorState'
import { BreadCrumbs } from '@/components/BreadCrumbs/BreadCrumbs'
import { DataTag } from '@/components/DataTag/DataTag'
import { PageLayout } from '@/components/templates/PageLayout/PageLayout'
import ClusterUpdateWorkflowStatusIcon, {
  ClusterUpdateStatus,
} from '@/features/ClusterUpdates/ClusterUpdateWorkflowStatus'
import { useGetBulkClusterUpdateDetails } from '@/generated/http-clients/v3ApiComponents'
import { clusterUpdatesRootRoute } from '@/router/rootRoutes'
import { Card, Table, Text } from '@chakra-ui/react'
import type { UseQueryResult } from '@tanstack/react-query'
import { createRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { clusterUpdateTableRoute } from './ClusterUpdateTable'
import SkeletonTable from './SkeletonTable'
import styles from './clusterUpdateTable.module.css'

export const clusterUpdateDetailsRoute = createRoute({
  getParentRoute: () => clusterUpdatesRootRoute,
  path: '$bulkUpdateId',
  component: () => <ClusterUpdateTaskDetails />,
})

export function ClusterUpdateTaskDetails() {
  const { t } = useTranslation()

  const params = clusterUpdateDetailsRoute.useParams()
  const bulkUpdateId = params.bulkUpdateId

  const clusterUpdateDetails = useGetBulkClusterUpdateDetails({
    pathParams: { bulkUpdateId },
  })

  const title = t('bulkUpdateDetails.title')
  const breadCrumbs = [
    { name: t('sidebar.hiveUpdates'), path: clusterUpdateTableRoute.to },
    { name: bulkUpdateId, path: '' },
  ]

  const skeletonWidths = [32, 100, 250, 100]

  const workflowState = clusterUpdateDetails?.data?.workflowState

  const confirmedClusters = workflowState?.confirmedClusters || []
  const failedClusters = workflowState?.failedClusters || []
  const currentCluster = workflowState?.currentCluster

  const clusterDetailsUpdates = (workflowState?.allClusters || []).map(
    (clusterId) => {
      if (confirmedClusters.includes(clusterId)) {
        return { clusterId, state: ClusterUpdateStatus.SUCCESS }
      }
      if (failedClusters.includes(clusterId)) {
        return { clusterId, state: ClusterUpdateStatus.FAILED }
      }
      if (clusterId === currentCluster) {
        return { clusterId, state: ClusterUpdateStatus.IN_PROGRESS }
      }
      return { clusterId, state: ClusterUpdateStatus.PENDING }
    },
  )

  return (
    <PageLayout title={title} beforeTitle={<BreadCrumbs paths={breadCrumbs} />}>
      <Card.Root w="100%">
        <Table.Root variant="outline">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeader width="32px" paddingRight="0" />
              <Table.ColumnHeader minWidth="min-content">
                {t('bulkUpdateDetails.table.columns.status')}
              </Table.ColumnHeader>
              <Table.ColumnHeader>
                {t('bulkUpdateDetails.table.columns.clusterId')}
              </Table.ColumnHeader>
              <Table.ColumnHeader />
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {clusterUpdateDetails.isLoading && (
              <SkeletonTable skeletonWidths={skeletonWidths} />
            )}

            {(clusterUpdateDetails.isError || !clusterUpdateDetails.data) && (
              <Table.Row>
                <Table.Cell colSpan={skeletonWidths.length}>
                  <ApiErrorState
                    apiResource={
                      clusterUpdateDetails as UseQueryResult<unknown, Error>
                    }
                    errorTitle={t('bulkUpdateDetails.table.errors.title')}
                    errorDescription={t(
                      'bulkUpdateDetails.table.errors.description',
                    )}
                  />
                </Table.Cell>
              </Table.Row>
            )}

            {clusterUpdateDetails.isSuccess &&
              !clusterDetailsUpdates.length && (
                <Table.Row>
                  <Table.Cell colSpan={skeletonWidths.length}>
                    <Text color="text.text-base">
                      {t('bulkUpdateDetails.table.noResults')}
                    </Text>
                  </Table.Cell>
                </Table.Row>
              )}

            {clusterUpdateDetails.isSuccess &&
              clusterDetailsUpdates.length > 0 &&
              clusterDetailsUpdates.map((details) => {
                return (
                  <Table.Row
                    key={details.clusterId}
                    className={styles.trAlignContentTop}
                  >
                    <Table.Cell
                      data-testid="icon-status"
                      className={styles.trAlignContentTopTdIcon}
                      width="2rem"
                    >
                      <ClusterUpdateWorkflowStatusIcon status={details.state} />
                    </Table.Cell>
                    <Table.Cell data-testid="status">
                      <Text fontWeight={400} lineHeight="1.5rem">
                        {details.state}
                      </Text>
                    </Table.Cell>
                    <Table.Cell data-testid="clusterId">
                      <DataTag
                        fontWeight={400}
                        hasCopy={true}
                        value={details.clusterId}
                      >
                        {details.clusterId}
                      </DataTag>
                    </Table.Cell>
                  </Table.Row>
                )
              })}
          </Table.Body>
        </Table.Root>
      </Card.Root>
    </PageLayout>
  )
}
